import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../utils/dateUtils';
import { notificationApi } from '../api';
import { Btn, EmptyState, Spinner } from '../components/ui';
import { useNavigate } from 'react-router-dom';

const TYPE_META = {
  TASK_ASSIGNED:               { icon: '📋', color: 'var(--color-primary)'  },
  DEADLINE_MISSED:             { icon: '⏰', color: 'var(--color-danger)'   },
  EXTENSION_REQUESTED:         { icon: '📅', color: 'var(--color-warning)'  },
  EXTENSION_APPROVED:          { icon: '✅', color: 'var(--color-success)'  },
  EXTENSION_REJECTED:          { icon: '❌', color: 'var(--color-danger)'   },
  ON_HOLD_ESCALATION:          { icon: '⚠️', color: 'var(--color-warning)'  },
  ADMIN_DEADLINE_CHANGED:      { icon: '🔄', color: 'var(--color-warning)'  },
  ADMIN_TASK_DELETED:          { icon: '🗑️', color: 'var(--color-danger)'   },
  EMPLOYEE_DELETION_REQUESTED: { icon: '👤', color: 'var(--color-warning)'  },
  ADMIN_ACTION_REVERSED:       { icon: '↩️', color: 'var(--color-primary)'  },
  DAILY_DIGEST:                { icon: '📊', color: 'var(--color-primary)'  },
  POOL_TASK_OVERDUE:           { icon: '⏰', color: 'var(--color-danger)'   },
  PASSWORD_RESET:              { icon: '🔑', color: 'var(--color-text-muted)'},
  GENERAL:                     { icon: '🔔', color: 'var(--color-primary)'  },
  REMINDER:                    { icon: '⏰', color: 'var(--color-warning)'  },
  HOLIDAY_CHANGE_REQUEST:      { icon: '🏖', color: 'var(--color-primary)'  },
  ANNOUNCEMENT:                { icon: '📢', color: 'var(--color-primary)'  },
};

function groupByDate(notifications) {
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  const today     = new Date(); today.setHours(0,0,0,0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);

  for (const n of notifications) {
    const d = parseUTC(n.createdAt); d.setHours(0,0,0,0);
    if (d.getTime() === today.getTime())     groups.Today.push(n);
    else if (d.getTime() === yesterday.getTime()) groups.Yesterday.push(n);
    else groups.Earlier.push(n);
  }
  return groups;
}

export default function Notifications() {
  const navigate              = useNavigate();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    notificationApi.getAll()
      .then(r => setItems(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Auto mark all as read when page is opened
  useEffect(() => {
    load();
    // Mark all read after a short delay so user sees what was unread
    const timer = setTimeout(() => {
      notificationApi.markAllRead().catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [load]);

  const markAllRead = async () => {
    await notificationApi.markAllRead();
    load();
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
      <Spinner size={28} />
    </div>
  );

  const unreadCount = items.filter(n => !n.read).length;
  const groups      = groupByDate(items);

  return (
    <div style={{ padding: '24px', maxWidth: '680px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)' }}>Notifications</h1>
          {unreadCount > 0 && (
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
              {unreadCount} unread
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <Btn variant="secondary" size="sm" onClick={markAllRead}>Mark all read</Btn>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon="🔔"
          title="No notifications yet"
          subtitle="Task assignments, deadline alerts, and daily digests will appear here."
        />
      ) : (
        Object.entries(groups).map(([label, notifs]) =>
          notifs.length === 0 ? null : (
            <div key={label} style={{ marginBottom: '24px' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-muted)', marginBottom: '10px' }}>
                {label}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {notifs.map(n => {
                  const meta = TYPE_META[n.type] || { icon: '🔔', color: 'var(--color-text-muted)' };
                  return (
                    <div
                      key={n.id}
                      onClick={() => n.relatedTaskId && navigate(`/tasks/${n.relatedTaskId}`)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                        padding: '12px 14px', borderRadius: 'var(--radius-md)',
                        background: n.read ? 'var(--color-surface)' : 'var(--color-primary-lighter)',
                        border: `1px solid ${n.read ? 'var(--color-border)' : 'rgba(30,58,95,0.15)'}`,
                        cursor: n.relatedTaskId ? 'pointer' : 'default',
                        transition: 'box-shadow 0.15s',
                      }}
                      onMouseEnter={e => { if (n.relatedTaskId) e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <span style={{ fontSize: '20px', flexShrink: 0, marginTop: '1px' }}>{meta.icon}</span>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '14px', color: 'var(--color-text)', lineHeight: 1.5 }}>{n.message}</p>
                        <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '3px' }}>
                          {timeAgo(n.createdAt)}
                          {n.relatedTaskId && <span style={{ color: 'var(--color-accent)', marginLeft: '8px' }}>View task →</span>}
                        </p>
                      </div>
                      {!n.read && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: meta.color, flexShrink: 0, marginTop: '6px' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )
      )}
    </div>
  );
}
